import axios from 'axios';
import { AdvisorInfo } from '../types/advisor';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080';

// In-memory cache for advisor configs
const configCache: Map<string, { data: AdvisorInfo; timestamp: number }> = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch advisor configuration from database (public endpoint, no auth required)
 * Falls back to static config if API fails
 *
 * @param customerId - Customer ID
 * @returns Advisor configuration or null
 */
export const fetchAdvisorConfig = async (customerId: string): Promise<AdvisorInfo | null> => {
  try {
    // Check cache first
    const cached = configCache.get(customerId);
    if (cached && (Date.now() - cached.timestamp) < CACHE_DURATION) {
      console.log(`[AdvisorConfig] Using cached config for ${customerId}`);
      return cached.data;
    }

    console.log(`[AdvisorConfig] Fetching config for ${customerId} from API`);

    // Fetch from API
    // Note: API_BASE_URL already includes /api, so we don't add it again
    const response = await axios.get(`${API_BASE_URL}/public/advisor-config/${customerId}`, {
      timeout: 5000 // 5 second timeout
    });

    const config = response.data as AdvisorInfo;

    // Cache the result
    configCache.set(customerId, {
      data: config,
      timestamp: Date.now()
    });

    return config;
  } catch (error) {
    console.error(`[AdvisorConfig] Failed to fetch config for ${customerId}:`, error);

    // Return cached data even if expired (better than nothing)
    const cached = configCache.get(customerId);
    if (cached) {
      console.warn(`[AdvisorConfig] Using expired cache for ${customerId}`);
      return cached.data;
    }

    // If API fails and no cache, return null (will fall back to static config)
    return null;
  }
};

/**
 * Clear the cache for a specific customer ID or all
 */
export const clearAdvisorConfigCache = (customerId?: string): void => {
  if (customerId) {
    configCache.delete(customerId);
    console.log(`[AdvisorConfig] Cleared cache for ${customerId}`);
  } else {
    configCache.clear();
    console.log(`[AdvisorConfig] Cleared all advisor config cache`);
  }
};
